// Hook (use-auth.js)
import React, { useState, useEffect, useContext, createContext } from 'react';
import axios from '../utils/axios';
import { login, getMe } from '../services/auth';

const AuthContext = createContext<any>({});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const auth = useProvideAuth();
	return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
	return useContext(AuthContext);
};

function useProvideAuth() {
	const [user, setUser] = useState<any>(undefined);
	const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
	const [emailVerificationData, setEmailVerificationData] = useState<{
		email: string;
		message?: string;
	} | null>(null);

	const signIn = async (email: string, password: string) => {
		try {
			const res = await login(email, password);
			const data = res.data.data;

			// Verificar si requiere verificación por email
			if (data.requiresEmailVerification) {
				console.log('Email verification required:', data);
				setEmailVerificationRequired(true);
				setEmailVerificationData({
					email: data.email,
					message: data.message
				});
				return; // No completar login aún
			}

			// Login completo directo (flujo existente)
			const { accessToken } = data.response.token;
			sessionStorage.setItem('token', accessToken);
			await getUserData();
			return res;
		} catch (error) {
			throw error;
		}
	};

	const signOut = () => {
		sessionStorage.removeItem('token');
		sessionStorage.removeItem('lastPagePatients');
		sessionStorage.removeItem('lastPageUsers');
		setUser(false);
		
		// Limpiar estados de email verification
		setEmailVerificationRequired(false);
		setEmailVerificationData(null);
	};

	const isAdmin = () => {
		return user.isAdmin;
	};

	const getUserData = async () => {
		try {
			const { data } = await getMe();
			const { data: userData } = data;
			// window.addEventListener("beforeunload", signOut);
			setUser(userData);
		} catch (error) {
			signOut();
		}
	};

	useEffect(() => {
		const accessToken = sessionStorage.getItem('token');
		if (accessToken) {
			getUserData();
		} else {
			setUser(false);
		}
		return () => {
			signOut();
		};
	}, []);

	const verifyEmailCode = async (code: string, email: string) => {
		try {
			const { verifyEmailCode: verifyEmailCodeService } = await import('../services/auth');
			const res = await verifyEmailCodeService(code, email);
			console.log('Email verification successful:', res);

			// Completar autenticación
			const { accessToken } = res.response.token;
			sessionStorage.setItem('token', accessToken);

			// Limpiar estados de verificación
			setEmailVerificationRequired(false);
			setEmailVerificationData(null);

			await getUserData();
		} catch (error) {
			console.error('Email verification failed:', error);
			throw error;
		}
	};

	const resendEmailCode = async (email: string) => {
		try {
			console.log('AuthContext: resendEmailCode called for email:', email);
			const { resendEmailCode: resendEmailCodeService } = await import('../services/auth');
			console.log('AuthContext: About to call service...');
			await resendEmailCodeService(email);
			console.log('AuthContext: Email code resent successfully');
		} catch (error) {
			console.error('AuthContext: Resend email code failed:', error);
			throw error;
		}
	};

	const clearEmailVerification = () => {
		setEmailVerificationRequired(false);
		setEmailVerificationData(null);
	};

	return {
		user,
		isAdmin,
		signIn,
		signOut,
		emailVerificationRequired,
		emailVerificationData,
		verifyEmailCode,
		resendEmailCode,
		clearEmailVerification,
	};
}
